/**
 * API Client for AI Analysis endpoints
 */

import axios from 'axios';
import { apiClient, handleApiError } from './apiClient';

export interface HealthScoreResponse {
  data: {
    overallScore: number;
    rating: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
    components: Array<{
      name: string;
      score: number;
      weight: number;
      explanation: string;
      dataAvailable?: boolean;
    }>;
    summary: string;
    recommendations: string[];
    disclaimer: string;
    calculatedAt: string;
  };
}

export interface AIQueryResponse {
  data: {
    question: string;
    answer: string;
    dataSources: string[];
    confidence: 'high' | 'medium' | 'low';
    disclaimer: string;
    answeredAt: string;
  };
}

export async function fetchHealthScore(): Promise<HealthScoreResponse['data']> {
  try {
    const response = await apiClient.get<HealthScoreResponse>('/ai/health-score');
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      if (status === 503 || status === 501) {
        throw new Error('AI service unavailable');
      }
      const data = error.response.data as Record<string, unknown>;
      const msg = (data?.message ?? data?.error) as string | undefined;
      if (msg && (msg.includes('API key') || msg.includes('ANTHROPIC') || msg.includes('not configured'))) {
        throw new Error(`AI service not configured: ${msg}`);
      }
    }
    return handleApiError(error, 'Failed to fetch health score');
  }
}

export async function queryAI(
  question: string,
  contextHint?: string
): Promise<AIQueryResponse['data']> {
  try {
    const response = await apiClient.post<AIQueryResponse>('/ai/query', {
      question,
      contextHint,
    });
    return response.data.data;
  } catch (error) {
    return handleApiError(error, 'Failed to get AI response');
  }
}
